import { useState } from 'react';
import { IconArrowsExchange, IconChartBar, IconChevronLeft, IconLock, IconArrowBackUp, IconPlus, IconRefresh, IconTrash, IconArrowRight } from '@tabler/icons-react';
import { Page, PageHeader } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { FIELDS, LOTS, LOTS_N, NS_COUNT, ERR_COUNT, PREV, SHEETS, TOTAL0, mapRows, num } from './customsDataData';
import { useT } from '@hana/i18n';
import './i18n';
import s from './customsData.module.css';

// Mint-CustomsData (1536): Số liệu hải quan · Nhập tờ khai (bước 1–2) + Lịch sử lô. Chỉ đọc; nút ghi báo toast.

function readState(): ScreenState {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : 'ready';
}

export function CustomsData() {
  const t = useT();
  const toastFn = useToast();
  const soon = () => toastFn({ msg: t('cd.noWrite') });
  const state = readState();
  const ro = state === 'noperm';
  const [tab, setTab] = useState<'imp' | 'hist'>('imp');
  const [step, setStep] = useState(1);
  const [sheet, setSheet] = useState(0);
  const [ov, setOv] = useState<Record<number, string>>({});
  const m = mapRows(ov);
  const rows = SHEETS[sheet]![1];
  const steps: [string, string][] = [
    [t('cd.s1'), t('cd.rowsN', { sheet: SHEETS[sheet]![0], n: num(rows) })],
    [t('cd.s2'), m.miss.length ? t('cd.missN', { n: m.miss.length }) : t('cd.allReq', { n: 6 })],
    [t('cd.s3'), t('cd.errN', { e: ERR_COUNT, n: NS_COUNT })],
    [t('cd.s4'), t('cd.lots')],
  ];
  const nextDis = (step === 2 && m.miss.length > 0) || (step === 1 && !rows);
  const cycle = (j: number) => setOv((o) => { const cur = o[j] ?? mapRows({}).rows[j]!.f; return { ...o, [j]: FIELDS[(FIELDS.indexOf(cur) + 1) % FIELDS.length]! }; });
  const ready = state === 'ready' || ro;

  return (
    <Page kind="table">
      <PageHeader
        lead={<a href="/ezy/service-tasks" className={s.back} aria-label={t('cd.backSvc')} title={t('cd.backSvc')}><IconChevronLeft size={17} aria-hidden="true" /></a>}
        title={t('cd.title')}
        tabs={
          <>
            <Segmented label={t('cd.section')} value={tab} onChange={setTab} items={[{ key: 'imp', label: t('cd.tImp') }, { key: 'hist', label: <>{t('cd.tHist')} <span className={s.n}>{LOTS_N}</span></> }]} />
            <a href="/ezy/market-share" className={s.ms} title={t('cd.msTip')}><IconChartBar size={16} aria-hidden="true" />{t('cd.msLink')}</a>
          </>
        }
        actions={
          <>
            <span className={s.tot}><b>{num(TOTAL0)}</b> {t('cd.totRows')}</span>
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} disabled={ro} onClick={() => { setTab('imp'); setStep(1); setOv({}); }}>{t('cd.newFile')}</Button>
          </>
        }
      />
      {ro && <div role="note" className={s.note}><IconLock size={16} aria-hidden="true" />{t('cd.viewOnly')}</div>}
      {state === 'loading' && <StateView state="loading" skeleton={<Skeleton />} />}
      {state === 'error' && <div className={s.stCard}><StateView state="error" title={t('cd.errT')} desc={t('cd.errD')} action={<Button onClick={() => location.reload()}>{t('cd.retry')}</Button>} /></div>}
      {state === 'empty' && <div className={s.stCard}><StateView state="empty" title={t('cd.emptyT')} desc={t('cd.emptyD')} action={<Button variant="primary" onClick={soon}>{t('cd.importFile')}</Button>} /></div>}
      {ready && (tab === 'hist' || (ro && tab === 'imp')) && (
        <section aria-label={t('cd.histSec')} className={s.hist}>
          <div className={s.histHead}>
            <b>{t('cd.histH')}</b>
            <button type="button" className={s.purge} disabled={ro} onClick={soon}><IconTrash size={16} aria-hidden="true" />{t('cd.purge')}</button>
          </div>
          <table className={s.tbl}>
            <thead><tr>{[t('cd.thLot'), t('cd.thPeriod'), t('cd.thRows'), t('cd.thErr'), t('cd.thBy'), t('cd.thStatus'), ''].map((c, i) => <th key={i}>{c}</th>)}</tr></thead>
            <tbody>
              {LOTS.map(([code, period, n, err, who, when]) => (
                <tr key={code}>
                  <td className={s.code}>{code}</td><td>{period}</td><td>{num(n)}</td>
                  <td className={err ? s.warnTx : s.lbTx}>{err}</td><td>{who} · {when}</td>
                  <td><span className={s.stOk}>{t('cd.written')}</span></td>
                  <td><button type="button" className={s.re} disabled={ro} onClick={soon}><IconArrowBackUp size={16} aria-hidden="true" />{t('cd.undoLot')}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {ready && tab === 'imp' && !ro && (
        <section aria-label={t('cd.impSec')} className={s.card}>
          <div className={s.steps}>
            {steps.map(([k, d], i) => {
              const n = i + 1;
              const done = n < step;
              const on = n === step;
              return (
                <button key={k} type="button" className={`${s.step} ${on ? s.stepOn : ''}`} onClick={() => { if (n <= step) setStep(n); }}>
                  <span className={`${s.dot} ${done ? s.dotOk : on ? s.dotOn : ''}`}>{done ? '✓' : n}</span>
                  <span><b>{k}</b><div className={s.lb12}>{d}</div></span>
                </button>
              );
            })}
          </div>
          {step === 1 && (
            <div className={s.two}>
              <div className={s.drop}>
                <b>{t('cd.dropH')}</b>
                <div className={s.file}>
                  <span className={s.xl}>XLSX</span>
                  <div className={s.fg}><div className={s.fn}>TK_XK_10_2026_tuan4.xlsx</div><div className={s.lb12}>{t('cd.fileMeta')}</div></div>
                  <button type="button" className={s.re} onClick={soon}><IconArrowsExchange size={16} aria-hidden="true" />{t('cd.swap')}</button>
                </div>
                <div className={s.lab}>{t('cd.pickSheet')}</div>
                <div role="radiogroup" aria-label={t('cd.sheetAria')} className={s.sheets}>
                  {SHEETS.map(([k, n], j) => <button key={k} type="button" role="radio" aria-checked={sheet === j} className={`${s.sh} ${sheet === j ? s.shOn : ''}`} onClick={() => setSheet(j)}>{k}<span className={s.shn}>{t('cd.rowsShort', { n: num(n) })}</span></button>)}
                </div>
              </div>
              <div className={s.prev}>
                <b>{t('cd.prevH', { sheet: SHEETS[sheet]![0] })}</b>
                <div className={s.pt}>
                  <div className={`${s.pr} ${s.ph}`}><span>{t('cd.pDecl')}</span><span>{t('cd.pDate')}</span><span>{t('cd.pExp')}</span><span>HS</span><span>{t('cd.pCountry')}</span></div>
                  {(rows ? PREV : []).map(([no, d, sh, hs, c]) => <div key={no} className={s.pr}><span>{no}</span><span>{d}</span><span className={s.ell}>{sh}</span><span>{hs}</span><span>{c}</span></div>)}
                </div>
              </div>
            </div>
          )}
          {step === 2 && (
            <>
              <div className={s.mh}><span className={s.tx2}>{t('cd.mapSum', { a: m.auto, b: m.manual })}<b className={m.miss.length ? s.err : s.ok}>{t('cd.missSum', { n: m.miss.length })}</b></span><Button size="sm" icon={<IconRefresh size={16} aria-hidden="true" />} onClick={() => setOv({})}>{t('cd.remap')}</Button></div>
              <div className={s.map}>
                <div className={`${s.mr} ${s.mrh}`}><span>{t('cd.colXl')}</span><span /><span>{t('cd.colField')}</span><span>{t('cd.colHow')}</span></div>
                {m.rows.map((r) => (
                  <div key={r.j} className={s.mr}>
                    <b>{r.col}</b><span className={s.lb12}>→</span>
                    <button type="button" className={`${s.fld} ${r.skip ? s.skip : ''}`} title={t('cd.cycleTip')} onClick={() => cycle(r.j)}>{r.field} ▾</button>
                    <span className={`${s.how} ${r.tone === 'ok' ? s.ok : r.tone === 'link' ? s.lk : s.lb12}`}>{r.how}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          {step >= 3 && <StateView state="empty" title={t('cd.stepEmptyT')} desc={t('cd.stepEmptyD')} />}
          <div className={s.foot}>
            {step > 1 ? <Button variant="ghost" onClick={() => setStep(step - 1)}>{t('cd.back')}</Button> : <span />}
            <Button variant="primary" disabled={nextDis || step >= 3} icon={<IconArrowRight size={16} aria-hidden="true" />} onClick={() => (step === 2 ? soon() : setStep(step + 1))}>{t('cd.next')}</Button>
          </div>
        </section>
      )}
    </Page>
  );
}
